/**
 * Faro bootstrap module (task 5.4) — real RUM/tracing bootstrap for the Snap
 * Reports frontend.
 *
 * Feature `telemetry-observability`, design component 7. This module owns the
 * Faro lifecycle behind a small, side-effect-guarded API:
 *
 * - `initTelemetry()` — gated by `VITE_TELEMETRY_ENABLED`, idempotent, and
 *   backed by a **dynamic `import()`** of `@grafana/faro-web-sdk` and
 *   `@grafana/faro-web-tracing` so the whole Faro SDK lands in a **separate
 *   lazy chunk**, never in the main bundle (Req 10.9, 15.4). Wired from
 *   `main.tsx` in task 5.5 (this task does not touch `main.tsx`).
 * - `setTelemetryUser()` / `clearTelemetryUser()` — session identity limited to
 *   `user_id` + `organization_id`, no PII (Req 10.3, 10.13, 14).
 * - `getFaro()` — unchanged `Faro | null` contract from task 5.2, now backed by
 *   the module singleton so the pure `Helper_de_Telemetria` keeps taking its
 *   safe no-op path whenever telemetry is off or not yet initialized.
 *
 * All application config is read from `import.meta.env.VITE_*` — there are **no
 * hardcoded** collector URLs, app names, versions or keys (Req 10.2). The Faro
 * browser `apiKey` is a build-time public value by nature (abuse control /
 * routing, not authentication) and is only ever referenced via `import.meta.env`
 * (Req 3.9); the real protection lives at the collector (CORS + rate limit +
 * payload limit).
 *
 * The `import type` below is erased at build time, so this module pulls **no**
 * runtime Faro code into the main bundle.
 */
import type { Faro } from "@grafana/faro-web-sdk";

/**
 * Module singleton. `null` means telemetry is disabled, not yet initialized, or
 * failed to initialize (all of which drive the safe no-op path). Never reset to
 * a second instance — see `initTelemetry` idempotency (Req 10.1).
 */
let faroInstance: Faro | null = null;

/**
 * Guards against concurrent `initTelemetry()` calls racing past the synchronous
 * `faroInstance` check before the first `await` resolves. Ensures **at most one**
 * bootstrap (and therefore at most one Faro instance) per page load (Req 10.1).
 */
let initPromise: Promise<void> | null = null;

/** Reads the telemetry flag. Env values are strings, so `"false"` must not be
 * treated as truthy — only the exact string `"true"` enables telemetry
 * (Req 10.8). */
function isTelemetryEnabled(): boolean {
  return import.meta.env.VITE_TELEMETRY_ENABLED === "true";
}

/**
 * Parses the optional session sampling rate. Returns `undefined` (Faro default)
 * for missing, empty, non-numeric or out-of-range `[0, 1]` values so a bad env
 * never breaks initialization.
 */
function parseSampleRate(raw: string | undefined): number | undefined {
  if (raw === undefined || raw === "") {
    return undefined;
  }
  const parsed = Number.parseFloat(raw);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 1) {
    return undefined;
  }
  return parsed;
}

/** Escapes a string for safe literal use inside a `RegExp`. */
function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Extracts the origin (`protocol//host`) from a URL string, or `null` when the
 * value is absent or unparseable. */
function toOrigin(raw: string | undefined): string | null {
  if (raw === undefined || raw === "") {
    return null;
  }
  try {
    return new URL(raw).origin;
  } catch {
    return null;
  }
}

/**
 * Builds the allowlist of the product's **own** API origins for
 * `TracingInstrumentation.propagateTraceHeaderCorsUrls`, so the `traceparent`
 * header is attached only to first-party requests and never to third parties
 * (Req 10.4, 10.11).
 *
 * Sources (design component 7): `VITE_REPORTS_API_URL`, `VITE_PDF_SERVICE_URL`,
 * `VITE_DOCX_SERVICE_URL`, plus the current same-origin — bundled builds serve
 * the API same-origin under `/reports/`, and in dev the API lives at
 * `VITE_REPORTS_API_URL`.
 *
 * Each origin becomes a `RegExp` anchored at the start and bounded by a path
 * separator or end (`^origin(/|$)`), so `https://api.example.com` matches
 * `https://api.example.com/reports/...` but not a look-alike host such as
 * `https://api.example.com.evil.com`.
 */
function buildApiOriginAllowlist(): RegExp[] {
  const origins = new Set<string>();

  if (typeof window !== "undefined") {
    origins.add(window.location.origin);
  }

  const candidates: Array<string | undefined> = [
    import.meta.env.VITE_REPORTS_API_URL,
    import.meta.env.VITE_PDF_SERVICE_URL,
    import.meta.env.VITE_DOCX_SERVICE_URL,
  ];

  for (const candidate of candidates) {
    const origin = toOrigin(candidate);
    if (origin !== null) {
      origins.add(origin);
    }
  }

  return [...origins].map((origin) => new RegExp(`^${escapeRegExp(origin)}(/|$)`));
}

/**
 * Performs the one-time Faro bootstrap. Kept private and wrapped in try/catch so
 * any failure (network, SDK, config) leaves `faroInstance` null and the app
 * fully operational — telemetry failure never breaks the app (Req 10.12).
 *
 * On HTTP 429 from the collector, Faro's `FetchTransport` drops the current
 * payload without retrying it (0 retries) and applies a backoff to subsequent
 * sends; pushes are best-effort, so the user never sees a telemetry error
 * (Req 3.6).
 */
async function bootstrap(): Promise<void> {
  try {
    // Dynamic import() => Faro lands in a separate lazy chunk (Req 10.9, 15.4).
    const { initializeFaro, getWebInstrumentations } = await import("@grafana/faro-web-sdk");
    const { TracingInstrumentation } = await import("@grafana/faro-web-tracing");

    faroInstance = initializeFaro({
      url: import.meta.env.VITE_FARO_COLLECTOR_URL, // same-origin /collect (Req 10.2)
      apiKey: import.meta.env.VITE_FARO_API_KEY, // public by nature (Req 3.9)
      app: {
        name: import.meta.env.VITE_FARO_APP_NAME, // "snap-reports" (Req 10.2)
        version: import.meta.env.VITE_APP_VERSION, // IMAGE_TAG (Req 10.2, 13.5)
        environment: import.meta.env.VITE_TELEMETRY_ENV,
      },
      instrumentations: [
        // Errors + WebVitals + Session; ConsoleInstrumentation omitted via
        // captureConsole:false (Req 10.5, 10.6).
        ...getWebInstrumentations({ captureConsole: false }),
        new TracingInstrumentation({
          instrumentationOptions: {
            // Attach traceparent only to first-party origins (Req 10.4, 10.11).
            propagateTraceHeaderCorsUrls: buildApiOriginAllowlist(),
          },
        }),
      ],
      // SessionInstrumentation (Req 10.6); optional per-session sampling.
      sessionTracking: {
        enabled: true,
        samplingRate: parseSampleRate(import.meta.env.VITE_FARO_SAMPLE_RATE),
      },
      // WebVitals carry the app meta (incl. app.version) for per-version
      // analysis (Req 13.5).
      trackWebVitalsAttribution: true,
    });
  } catch {
    // Fail-open: leave telemetry off for this session (Req 10.12).
    faroInstance = null;
  }
}

/**
 * Initializes Faro exactly once per page load.
 *
 * No-ops (resolving immediately) when the `Flag_de_Telemetria` is off (Req 10.8)
 * or Faro is already initialized (Req 10.1). Concurrent calls share the single
 * in-flight bootstrap promise, so no more than one Faro instance is ever created
 * (Req 10.1). Never throws and never blocks the caller (Req 10.12).
 */
export function initTelemetry(): Promise<void> {
  if (!isTelemetryEnabled()) {
    return Promise.resolve();
  }
  if (faroInstance !== null) {
    return Promise.resolve();
  }
  if (initPromise === null) {
    initPromise = bootstrap();
  }
  return initPromise;
}

/**
 * Identifies the telemetry session using **only** `user_id` and
 * `organization_id` — never email, name or any other PII (Req 10.3, 14.1, 14.2).
 * Safe no-op when Faro is off or not initialized.
 */
export function setTelemetryUser(userId: string, organizationId: string): void {
  if (faroInstance === null) {
    return;
  }
  faroInstance.api.setUser({
    id: userId,
    attributes: { organization_id: organizationId },
  });
}

/**
 * Clears the telemetry session identity on logout, so no signal emitted
 * afterwards carries `user_id` or `organization_id` (Req 10.13, 14.3, 14.8).
 * Safe no-op when Faro is off or not initialized.
 */
export function clearTelemetryUser(): void {
  if (faroInstance === null) {
    return;
  }
  faroInstance.api.resetUser();
}

/**
 * Associates the current Faro "view" with a route **pattern** (never a
 * concrete URL/id), so Web_Vitals reported after this call are attributed to
 * the pattern (Req 12.7, 13.5). Called from the navigation instrumentation
 * (task 5.16, `~/components/router/Router.tsx`). Safe no-op when Faro is off
 * or not initialized; never throws.
 */
export function setTelemetryView(routePattern: string): void {
  if (faroInstance === null) {
    return;
  }
  try {
    faroInstance.api.setView({ name: routePattern });
  } catch {
    // Telemetry is best-effort: never propagate a view-attribution failure.
  }
}

/**
 * Returns the initialized Faro instance, or `null` when telemetry is disabled,
 * not yet initialized, or failed to initialize.
 *
 * The explicit `Faro | null` return annotation is the stable contract the
 * `Helper_de_Telemetria` (task 5.2) guards against; this task supplies the real
 * instance without changing the signature.
 */
export function getFaro(): Faro | null {
  return faroInstance;
}
